import { createResource, deleteResource, getResource, postgRESTApiUrl } from "./api";

export const COMMENTS_PAGE_SIZE = 20;
// Upper bound on the batched reply fetch, so a busy card can't return an
// unbounded reply payload (two-level threading caps depth, not breadth).
// Per-thread lazy reply loading is the fast-follow if threads outgrow this.
export const COMMENTS_REPLIES_LIMIT = 200;

// Top-level comments for a commentable, newest-first, keyset-paginated on
// (created_at, id) — same idiom as messages.js. Reads the PostgREST `comments`
// view (author fields + [deleted] masking baked in).
//
// Applies the rendered-node rule server-side — a top-level comment is returned
// iff it isn't deleted OR still has a kept reply — so the view never hands back
// childless tombstones the UI would only hide (which otherwise leaves a page
// short and can produce a "Load more" that yields nothing). This is deliberately
// scoped to the top-level list; listRepliesForRoots and getById must NOT filter
// this way (getById needs to return a tombstone-with-replies row for reconcile).
const listForCommentable = async (
  commentableType,
  commentableId,
  { cursor = null, limit = COMMENTS_PAGE_SIZE } = {},
) => {
  const cursorFilter = cursor
    ? [["or", `(created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id}))`]]
    : [];
  const params = new URLSearchParams([
    ["commentable_type", `eq.${commentableType}`],
    ["commentable_id", `eq.${commentableId}`],
    ["parent_comment_id", "is.null"],
    // rendered-node rule: is_deleted=false OR has_kept_replies=true.
    ["or", "(is_deleted.is.false,has_kept_replies.is.true)"],
    ["order", "created_at.desc,id.desc"],
    ["limit", String(limit)],
    ...cursorFilter,
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/comments?${params}`);
};

// Flattened reply threads for a set of top-level comments, oldest-first — one
// batched read for a page of top-level comments (avoids an N+1). Caller groups
// the rows by root_comment_id. Bounded by COMMENTS_REPLIES_LIMIT, and skips
// tombstoned replies (safe: replies are leaves under the two-level cap, so a
// deleted reply is never a rendered node) — this also keeps the id set handed
// to the engagement RPC within its own cap.
const listRepliesForRoots = async (rootCommentIds, { limit = COMMENTS_REPLIES_LIMIT } = {}) => {
  if (!rootCommentIds || rootCommentIds.length === 0) return { data: [] };
  const params = new URLSearchParams([
    ["root_comment_id", `in.(${rootCommentIds.join(",")})`],
    ["is_deleted", "is.false"],
    ["order", "created_at.asc,id.asc"],
    ["limit", String(limit)],
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/comments?${params}`);
};

// A single comment row from the `comments` view by id — carries the view's
// author fields + [deleted] masking. Used to reconcile one just-created row into
// the loaded thread in place (the Rails write only returns the bare id, not the
// view projection), instead of reloading the whole thread.
const getById = async (id) => {
  const params = new URLSearchParams([
    ["id", `eq.${id}`],
    ["limit", "1"],
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/comments?${params}`);
};

// Writes go through the Rails endpoint (JSON:API envelope); createResource/
// deleteResource inject auth + token refresh.
// `richBody` is the structured content document (array of {type:"text",value} /
// {type:"mention",profile_id} segments). The server derives the plaintext body from it.
const create = async ({ richBody, commentableType, commentableId, parentCommentId = null }) => {
  const attributes = {
    rich_body: richBody,
    commentable_type: commentableType,
    commentable_id: String(commentableId),
  };
  if (parentCommentId) attributes.parent_comment_id = String(parentCommentId);
  return createResource("/interact/api/comments", { data: { attributes } });
};

const destroy = async (id) => deleteResource(`/interact/api/comments/${id}`);

// The mentionable set for a commentable — the composer's @mention typeahead source. Reads the
// `comment_participants` view: the commentable's genuine author (a FeedPost's poster; none for a
// Story Idea card) plus its distinct kept commenters, each with display fields + an is_author
// flag, already filtered to active/displayable profiles server-side. Scoped to the commentable;
// the view is viewer-independent, so the caller (CommentsSection) excludes the current user.
const listParticipants = async (commentableType, commentableId) => {
  const params = new URLSearchParams([
    ["commentable_type", `eq.${commentableType}`],
    ["commentable_id", `eq.${commentableId}`],
    ["order", "is_author.desc,name.asc"],
  ]);
  return getResource(`${postgRESTApiUrl()}/rest/v1/comment_participants?${params}`);
};

export default {
  listForCommentable,
  listRepliesForRoots,
  listParticipants,
  getById,
  create,
  destroy,
  COMMENTS_PAGE_SIZE,
};
